import { useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import type { ViewTableInstance } from "./core/table";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { getSelectAllCheckboxState } from "./core/selectAll";
import styles from "./ViewTable.module.scss";

/**
 * Opt-in control that turns the header checkbox into a true "all matching"
 * toggle, independent of how many pages/rows are currently loaded. When a
 * consumer supplies it, the header reflects and toggles the full server-side
 * match set instead of TanStack's loaded page rows.
 */
export interface SelectAllMatchingControl {
    /** The full server-side match count (matchingTotal). */
    total: number;
    /** All matching selected (no per-row narrowing). */
    isChecked: boolean;
    /** A subset selected (included ids, or exclusions). */
    isIndeterminate: boolean;
    /** Select-all-matching ⇄ clear. */
    onToggle: () => void;
}

export interface ViewTableCheckboxHeaderProps<TData extends RowData> {
    readonly table: ViewTableInstance<TData>;
    readonly control?: SelectAllMatchingControl;
    /** Read fresh by `ViewTable`; see `ViewTableLeafHeaderContext`. */
    readonly isAllPageRowsSelected: boolean;
    readonly isSomePageRowsSelected: boolean;
}

export const ViewTableCheckboxHeader = <TData extends RowData>({
    table,
    control,
    isAllPageRowsSelected,
    isSomePageRowsSelected,
}: ViewTableCheckboxHeaderProps<TData>) => {
    const checkboxRef = useRef<HTMLElement>(null);
    const { isChecked, isPartiallyChecked } = getSelectAllCheckboxState({
        control,
        isAllPageRowsSelected,
        isSomePageRowsSelected,
    });
    return (
        <span onClick={(event) => event.stopPropagation()}>
            <Checkbox
                ref={checkboxRef}
                className={styles.selectCheckbox}
                checked={isChecked}
                indeterminate={isPartiallyChecked}
                onCheckedChange={(checked) => {
                    if (control) {
                        control.onToggle();
                    } else {
                        table.toggleAllPageRowsSelected(checked);
                    }
                    // Drop focus from the checkbox so single-letter hotkeys
                    // (e.g. `c` in SimilarTransactionsUpdateDrawer) keep firing.
                    checkboxRef.current?.blur();
                }}
            />
        </span>
    );
};
ViewTableCheckboxHeader.displayName = "ViewTableCheckboxHeader";
